import { chooseSite, emptyEvent, noPlace, startingAttendees, type DealMeetingPerson, type EventDraft, type EventReminderAnswer, type InternalAttendee, type PagedRecord } from '@bms2/shared';
import { attendeeNamesOf, linkedSearch, offersCancelMeeting, placeSearch, toDraft, type CalendarEventRow } from './CalendarPage';
import { EventModal, type PlaceOption } from '../../components/modals/EventModal';
import { useAddPlace } from './AddPlace';
import { PAGES_PATH, post, useApi, useRows, put } from './api';
import { usePersonSearch } from '../settings/api';
import { useSiteCascade } from './useSiteCascade';

/**
 * The Upcoming Events tile's + Add Event (piece 13, scenario B9): the one
 * Add/Edit Event modal, opened with the record already tagged, so the event
 * lands in this tile and on the calendar from one save. 🔒 It is the Calendar
 * page's own window (T362): the same places, the same internal attendees, the
 * same people search and the same names, with the record tagged and the
 * creator and the record's person among the attendees from the start.
 */
export interface RecordEventModalProps {
  open: boolean;
  recordType: PagedRecord;
  recordId: string;
  ownerPersonId: string;
  timeZone: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  onError?: (message: string) => void;
  openerId?: string | null;
  /** An Upcoming Events row being edited: the modal opens on it and saves through the calendar's own update (decision 0108). */
  editing?: CalendarEventRow | null;
  /** The person this record is or names: a new event carries them and the owner as attendees from the start (product owner, 2026-09-11). */
  associatedPerson?: { personId: string; name: string } | null;
  /**
   * 🔒 Set Meeting on a Deal's Intent card (decision 6001): a new event opens with
   * this title, on the Deal's site, the Physical kind, and the Deal's people among
   * the attendees beside the creator. Absent, a new event opens empty as before.
   */
  preset?: { subject: string; siteId: string | null; people: readonly DealMeetingPerson[] } | null;
}

export function RecordEventModal({ open, recordType, recordId, ownerPersonId, timeZone, apiBaseUrl = '', onCancel, onSaved, onError, openerId = null, editing = null, associatedPerson = null, preset = null }: RecordEventModalProps) {
  const places = useRows<PlaceOption>(open ? `${PAGES_PATH}/calendar/places` : null, apiBaseUrl);
  const attendees = useRows<InternalAttendee>(open ? `${PAGES_PATH}/calendar/attendees` : null, apiBaseUrl);
  // 🔒 The viewer's own reminder of the event edited, or the channels alone for a new one (decision 1081).
  const reminder = useApi<EventReminderAnswer>(open ? `${PAGES_PATH}/calendar/reminder/own${editing ? `?eventId=${editing.id}` : ''}` : null, apiBaseUrl);
  // Add New on the Place field opens the + Add Site combo modal over the event (decision 0348).
  const addPlace = useAddPlace(apiBaseUrl, openerId);

  const resolveSiteCascade = useSiteCascade(apiBaseUrl);

  const searchPeople = usePersonSearch(apiBaseUrl);

  const save = async (draft: EventDraft) => {
    const body = { draft: { ...draft, taggedRecordType: recordType, taggedRecordId: recordId } };
    const answer = editing
      ? await put(`${PAGES_PATH}/calendar/events/${editing.id}`, { ...body, scope: null, occurrenceStartsAt: editing.occurrenceStartsAt ?? null }, apiBaseUrl)
      : await post(`${PAGES_PATH}/calendar/events`, body, apiBaseUrl);
    if (!answer.ok) {
      onError?.(answer.message);
      return;
    }
    onSaved();
    onCancel();
  };

  /** Cancel meeting: the row is kept and stamped, never deleted (decision 0262). */
  const cancelMeeting = async () => {
    if (!editing) return;
    const answer = await post(`${PAGES_PATH}/calendar/events/${editing.id}/cancel`, { scope: null, occurrenceStartsAt: editing.occurrenceStartsAt ?? null }, apiBaseUrl);
    if (!answer.ok) {
      onError?.(answer.message);
      return;
    }
    onSaved();
    onCancel();
  };

  const initial: EventDraft = editing
    ? toDraft(editing, ownerPersonId, timeZone)
    : preset
      ? { ...emptyEvent(ownerPersonId, timeZone), subject: preset.subject, eventType: 'Physical', place: preset.siteId ? chooseSite(preset.siteId) : noPlace(), taggedRecordType: recordType, taggedRecordId: recordId, attendeePersonIds: [...new Set([...startingAttendees(ownerPersonId, null), ...preset.people.map((person) => person.personId)])] }
      : { ...emptyEvent(ownerPersonId, timeZone), place: noPlace(), taggedRecordType: recordType, taggedRecordId: recordId, attendeePersonIds: startingAttendees(ownerPersonId, associatedPerson) };
  /**
   * 🔨 A person off the internal list, the record's own contact among them, is named
   * here so the pill reads the name and never the id (T362): the edited event's own
   * attendees, and the record's person. A person picked from the search is named by
   * the window itself as they are picked.
   */
  const names = { ...attendeeNamesOf(editing), ...(associatedPerson ? { [associatedPerson.personId]: associatedPerson.name } : {}), ...Object.fromEntries((preset?.people ?? []).map((person) => [person.personId, person.name])) };

  return (
    <>
      <EventModal
        key={open ? `open:${editing?.id ?? 'new'}` : 'closed'}
        open={open}
        initial={initial}
        ownerPersonId={ownerPersonId}
        organizerTimeZone={timeZone}
        places={places.state.kind === 'ready' ? places.state.data : []}
        searchPlaces={placeSearch(apiBaseUrl)}
        attendees={attendees.state.kind === 'ready' ? attendees.state.data : []}
        attendeeNames={names}
        resolveSiteCascade={resolveSiteCascade}
        searchCompanies={linkedSearch(apiBaseUrl)}
        searchPeople={searchPeople}
        onAddNewPlace={addPlace.onAddNewPlace}
        readMapsLink={addPlace.readMapsLink}
        existing={editing ? { parentEventId: editing.parentEventId ?? null, recurrenceRule: editing.recurrenceRule ?? null, cancelledAt: editing.cancelledAt ?? null } : null}
        reminder={reminder.state.kind === 'ready' ? reminder.state.data : null}
        onSave={(draft) => void save(draft)}
        {...(editing && offersCancelMeeting(editing) ? { onDelete: () => void cancelMeeting() } : {})}
        onCancel={onCancel}
        openerId={openerId}
      />
      {addPlace.siteModal}
    </>
  );
}
